import {customElement, property, state} from "lit/decorators.js";
import {UnLitElement} from "../../common/elements";
import {Dictionary} from "../../page/elements/dictionary";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {eopCustomEvent, KEYS} from "../../common/utils/events";
import type {Maybe} from "../../bootstrap/common/types";
import {cancelableWaitTime, type CancelableWaitTime} from "../../common/utils/promises";
import {EopErrorHandler} from "../../common/errorHandler";
import {resolve} from "../../container";

export type CareerGptMessageReactionEvent = CustomEvent<CareerGptMessageReactionEventData>;
export type CareerGptMessageFeedbackInputEvent = CustomEvent<CareerGptMessageFeedbackInputEventData>;

export const CAREER_GPT_MESSAGE_REACTION_EVENT = "career-gpt-message-reaction";
export const CAREER_GPT_MESSAGE_FEEDBACK_INPUT_EVENT = "career-gpt-message-feedback-input";
const THANK_YOU_MESSAGE_DURATION = 10000;

export type CareerGptMessageReactionEventData = {
    reaction: boolean;
    messageId: string;
};

export type CareerGptMessageFeedbackInputEventData = {
    feedbackMessage: string;
    messageId: string;
};

@customElement("eop-career-gpt-message-rating")
export class CareerGptMessageRating extends UnLitElement {

    @state()
    private reaction: Maybe<boolean>;
    @state()
    private feedbackCommentIsSubmitted: boolean = false;
    @state()
    private feedbackFormIsVisible: boolean = false;
    @state()
    private feedbackText: string = "";

    private thanksWait: Maybe<CancelableWaitTime>;

    @property({attribute: "message-id", type: String})
    public messageId: Maybe<string>;

    @property({attribute: false})
    public messageReaction: Maybe<boolean>;

    private dictionary: Dictionary;

    public connectedCallback(): void {
        super.connectedCallback();

        this.dictionary = Dictionary.of(this);
    }

    public disconnectedCallback(): void {
        this.cancelThanksWait();
        super.disconnectedCallback();
    }

    //TODO textarea has row="4" but ACs say it should grow with content. Clarify!
    public render(): TemplateResult {
        const ratingLocked = this.reaction !== undefined;
        const ratingWasPositive = this.reaction === true;
        const ratingWasNegative = this.reaction === false;
        return html`
            <div class="chat-message-rating" ?rateable=${this.messageId !== undefined}>
                <button type="button"
                        class="thumbs-up"
                        ?selected=${ratingWasPositive}
                        ?disabled=${ratingLocked}
                        aria-label=${this.dictionary.translate("MSG_THUMBS_UP_LABEL")}
                        aria-pressed=${ratingWasPositive}
                        @click=${() => this.rateResponse(true)}
                ><eop-svg-sprite-icon icon-id="thumbs_up"></eop-svg-sprite-icon></button>
                <button type="button"
                        class="thumbs-down"
                        ?selected=${ratingWasNegative}
                        ?disabled=${ratingLocked}
                        aria-label=${this.dictionary.translate("MSG_THUMBS_DOWN_LABEL")}
                        aria-pressed=${ratingWasNegative}
                        @click=${() => this.rateResponse(false)}
                ><eop-svg-sprite-icon icon-id="thumbs_down"></eop-svg-sprite-icon></button>
            </div>
            ${ratingWasNegative && this.feedbackFormIsVisible && !this.feedbackCommentIsSubmitted ? html`
                <div class="negative-feedback">
                    <div class="negative-feedback-header">
                        <strong>${this.dictionary.translate("CAREER_GPT_FEEDBACK_QUESTION")}</strong>
                        <button type="button"
                                class="feedback-close"
                                aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                                @click=${this.discardFeedback}
                        ><eop-svg-sprite-icon icon-id="close"></eop-svg-sprite-icon></button>
                    </div>
                    <div class="feedback-input-grow-container">
                        <textarea class="feedback-input"
                                  maxlength="150"
                                  placeholder=${this.dictionary.translate("CAREER_GPT_FEEDBACK_PLACEHOLDER")}
                                  aria-label=${this.dictionary.translate("CAREER_GPT_FEEDBACK_PLACEHOLDER")}
                                  .value=${this.feedbackText}
                                  @input=${this.updateFeedbackText}
                                  @keydown=${this.keydownOnFeedbackInput}
                        ></textarea>
                    </div>
                    <div class="feedback-motivation">${this.dictionary.translate("CAREER_GPT_FEEDBACK_MOTIVATION_TEXT")}</div>
                    <div class="negative-feedback-footer">
                        <button type="button"
                                class="feedback-submit"
                                ?disabled=${this.feedbackText.trim().length === 0}
                                aria-label=${this.dictionary.translate("CAREER_GPT_FEEDBACK_SUBMIT_BUTTON")}
                                @click=${this.submitFeedback}
                        >${this.dictionary.translate("CAREER_GPT_FEEDBACK_SUBMIT_BUTTON")}</button>
                        <div class="feedback-counter">${this.feedbackText.length} / 150</div>
                    </div>
                </div>
            ` : null}
            ${ratingWasNegative && this.feedbackFormIsVisible && this.feedbackCommentIsSubmitted ? html`
                <div class="negative-feedback thanks">
                    <strong>${this.dictionary.translate("CAREER_GPT_FEEDBACK_THANK_YOU")}</strong>
                    <button type="button"
                            class="feedback-close"
                            aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                            @click=${this.discardFeedback}
                    ><eop-svg-sprite-icon icon-id="close"></eop-svg-sprite-icon></button>
                </div>
            ` : null}
        `;
    }

    protected willUpdate(changedProperties: PropertyValues): void {
        if (changedProperties.has("messageId") || (changedProperties.has("messageReaction") && this.reaction !== this.messageReaction)) {
            this.reaction = this.messageReaction;
            this.discardFeedback();
        }
    }

    public discardFeedback(): void {
        this.cancelThanksWait();
        this.feedbackCommentIsSubmitted = false;
        this.feedbackFormIsVisible = false;
        this.feedbackText = "";
    }

    private rateResponse(reaction: boolean): void {
        if (this.reaction !== undefined || !this.messageId) {
            return;
        }

        this.reaction = reaction;
        this.feedbackFormIsVisible = !reaction;
        this.dispatchEvent(eopCustomEvent(CAREER_GPT_MESSAGE_REACTION_EVENT, {reaction, messageId: this.messageId}));
    }

    private updateFeedbackText(event: Event): void {
        const input = event.target as HTMLTextAreaElement;
        this.feedbackText = input.value;
    }

    private keydownOnFeedbackInput(event: KeyboardEvent): void {
        if (event.key === KEYS.ENTER && !event.shiftKey) {
            event.preventDefault();
            this.submitFeedback();
        }
    }

    private submitFeedback(): void {
        const feedbackMessage = this.feedbackText.trim();
        if (feedbackMessage.length === 0) {
            return;
        }
        this.feedbackCommentIsSubmitted = true;
        this.thanksWait = cancelableWaitTime(THANK_YOU_MESSAGE_DURATION);
        void this.thanksWait.promise.then(() => this.discardFeedback())
            .catch(error => resolve(EopErrorHandler).report(error));
        this.dispatchEvent(eopCustomEvent(CAREER_GPT_MESSAGE_FEEDBACK_INPUT_EVENT, {feedbackMessage, messageId: this.messageId}));
    }

    private cancelThanksWait(): void {
        this.thanksWait?.cancel();
        this.thanksWait = undefined;
    }
}